import { Scenario, ScenarioNode, ScenarioNodeOption, ScenarioNodeType } from "types/scenario";

export type FlowBranch = {
  optionIndex: number;
  option: ScenarioNodeOption;
  items: FlowItem[];
};

export type FlowItem = {
  node: ScenarioNode;
  branches?: FlowBranch[];
};

export type FlowParent = {
  node: ScenarioNode;
  optionIndex: number;
};

export const sortNodes = (nodes?: ScenarioNode[]) =>
  [...(nodes ?? [])].sort((left, right) => left.position - right.position);

export const countBranches = (nodes?: ScenarioNode[]) =>
  (nodes ?? []).reduce((total, node) => {
    if (node.type !== ScenarioNodeType.Choice) {
      return total;
    }

    return total + (node.options?.length ?? 0);
  }, 0);

export const getPreferredNodeId = (nodes?: ScenarioNode[], startNodeKey?: string) => {
  const list = sortNodes(nodes);

  if (!list.length) {
    return null;
  }

  if (startNodeKey) {
    const startNode = list.find((node) => node.key === startNodeKey);

    if (startNode) {
      return startNode.id;
    }
  }

  return list[0].id;
};

export const getNodeCount = (nodeCount?: number, nodes?: ScenarioNode[]) =>
  nodeCount ?? nodes?.length ?? 0;

export const getNextNodePosition = (nodes?: ScenarioNode[]) => {
  if (!nodes?.length) {
    return 0;
  }

  return Math.max(...nodes.map((node) => node.position)) + 1;
};

export const collectUsedKeys = (nodes?: ScenarioNode[]) => {
  const keys = new Set<string>();

  (nodes ?? []).forEach((node) => {
    if (node.key) {
      keys.add(node.key);
    }

    if (node.prevNodeKey) {
      keys.add(node.prevNodeKey);
    }

    if (node.nextNodeKey) {
      keys.add(node.nextNodeKey);
    }

    (node.options ?? []).forEach((option) => {
      if (option.nextNodeKey) {
        keys.add(option.nextNodeKey);
      }
    });
  });

  return keys;
};

export const getNextNodeKey = (nodes?: ScenarioNode[]) => {
  const used = collectUsedKeys(nodes);
  let next = 1;

  while (used.has(`node_${next}`)) {
    next += 1;
  }

  return `node_${next}`;
};

export const isNewNode = (node: Pick<ScenarioNode, "id">) => node.id < 0;

export const isMissingNode = (node: Pick<ScenarioNode, "id">) => node.id === 0;

const fromOption = (option: ScenarioNodeOption): ScenarioNode => ({
  id: 0,
  createdAt: 0,
  updatedAt: 0,
  isDeleted: false,
  deletedAt: 0,
  deletedBy: "",
  label: option.label,
  key: option.nextNodeKey,
  type: ScenarioNodeType.Message,
  content: option.label,
  variableKey: "",
  prevNodeKey: "",
  nextNodeKey: "",
  position: option.position,
  options: [],
  scenario: {} as Scenario,
});

export const findParent = (nodes: ScenarioNode[] | undefined, key: string): FlowParent | null => {
  if (!key) {
    return null;
  }

  for (const node of nodes ?? []) {
    const optionIndex = node.options?.findIndex((option) => option.nextNodeKey === key) ?? -1;

    if (optionIndex >= 0) {
      return { node, optionIndex };
    }

    if (node.type !== ScenarioNodeType.Choice && node.nextNodeKey === key) {
      return { node, optionIndex: -1 };
    }
  }

  return null;
};

export const getAncestorKeys = (nodes: ScenarioNode[] | undefined, key: string) => {
  const ancestors = new Set<string>();
  const visited = new Set<string>();
  let current = key;

  while (current && !visited.has(current)) {
    visited.add(current);
    const parent = findParent(nodes, current);

    if (parent) {
      ancestors.add(parent.node.key);
      current = parent.node.key;
      continue;
    }

    const node = (nodes ?? []).find((item) => item.key === current);

    if (!node?.prevNodeKey) {
      break;
    }

    ancestors.add(node.prevNodeKey);
    current = node.prevNodeKey;
  }

  return ancestors;
};

export const getDescendantKeys = (nodes: ScenarioNode[] | undefined, startKey: string) => {
  const byKey = new Map((nodes ?? []).map((node) => [node.key, node]));
  const keys = new Set<string>();

  const walk = (key: string) => {
    if (!key || keys.has(key)) {
      return;
    }

    keys.add(key);
    const node = byKey.get(key);

    if (!node) {
      return;
    }

    if (node.type === ScenarioNodeType.Choice) {
      (node.options ?? []).forEach((option) => walk(option.nextNodeKey));
      return;
    }

    walk(node.nextNodeKey);
  };

  walk(startKey);

  return keys;
};

export const getChoiceOptionBlockedKeys = (
  nodes: ScenarioNode[] | undefined,
  choice: ScenarioNode,
  optionIndex: number,
) => {
  const blocked = new Set<string>();

  (choice.options ?? []).forEach((option, index) => {
    if (index === optionIndex || !option.nextNodeKey) {
      return;
    }

    getDescendantKeys(nodes, option.nextNodeKey).forEach((key) => blocked.add(key));
  });

  return blocked;
};

export const getChoiceLinkError = (nodes: ScenarioNode[], node: ScenarioNode) => {
  if (node.type !== ScenarioNodeType.Choice) {
    return null;
  }

  const ancestors = getAncestorKeys(nodes, node.key);
  ancestors.add(node.key);

  for (let index = 0; index < (node.options ?? []).length; index += 1) {
    const nextNodeKey = node.options?.[index]?.nextNodeKey;

    if (!nextNodeKey) {
      continue;
    }

    if (ancestors.has(nextNodeKey)) {
      return "ancestor" as const;
    }

    if (getChoiceOptionBlockedKeys(nodes, node, index).has(nextNodeKey)) {
      return "branch" as const;
    }
  }

  return null;
};

export const hasInvalidNodeKeys = (nodes: ScenarioNode[]) => {
  const seen = new Set<string>();

  return nodes.some((node) => {
    const key = node.key.trim();

    if (!key || seen.has(key)) {
      return true;
    }

    seen.add(key);
    return false;
  });
};

export const renameNodeKey = (nodes: ScenarioNode[], from: string, to: string) => {
  if (!from || !to || from === to) {
    return nodes;
  }

  return nodes.map((node) => ({
    ...node,
    key: node.key === from ? to : node.key,
    prevNodeKey: node.prevNodeKey === from ? to : node.prevNodeKey,
    nextNodeKey: node.nextNodeKey === from ? to : node.nextNodeKey,
    options: (node.options ?? []).map((option) => ({
      ...option,
      nextNodeKey: option.nextNodeKey === from ? to : option.nextNodeKey,
    })),
  }));
};

const linkTargets = (node: ScenarioNode) => {
  if (node.type === ScenarioNodeType.Choice) {
    return (node.options ?? []).map((option) => option.nextNodeKey).filter(Boolean);
  }

  return node.nextNodeKey ? [node.nextNodeKey] : [];
};

export const relinkAfterEdit = (nodes: ScenarioNode[], next: ScenarioNode) => {
  const current = nodes.find((node) => node.id === next.id);

  if (!current) {
    return nodes;
  }

  let list = nodes.map((node) => (node.id === next.id ? next : node));

  if (current.key !== next.key) {
    list = renameNodeKey(list, current.key, next.key);
  }

  const oldTargets = new Set(linkTargets(current));
  const newTargets = new Set(linkTargets(next));
  const parentKey = next.key;

  if (oldTargets.size === 0 && newTargets.size === 0) {
    return list;
  }

  return list.map((node) => {
    if (oldTargets.has(node.key) && !newTargets.has(node.key) && node.prevNodeKey === parentKey) {
      return { ...node, prevNodeKey: "" };
    }

    if (newTargets.has(node.key)) {
      return { ...node, prevNodeKey: parentKey };
    }

    return node;
  });
};

export const buildFlow = (
  nodes: ScenarioNode[] | undefined,
  startNodeKey: string,
): { items: FlowItem[]; orphans: ScenarioNode[] } => {
  const list = nodes ?? [];
  const byKey = new Map(list.map((node) => [node.key, node]));
  const reached = new Set<string>();

  const walkChain = (startKey: string, ancestors: Set<string>): FlowItem[] => {
    const items: FlowItem[] = [];
    const visited = new Set(ancestors);
    let key = startKey;

    while (key && byKey.has(key) && !visited.has(key)) {
      visited.add(key);
      reached.add(key);
      const node = byKey.get(key) as ScenarioNode;

      if (node.type === ScenarioNodeType.Choice) {
        items.push({
          node,
          branches: (node.options ?? []).map((option, optionIndex) => ({
            optionIndex,
            option,
            items: option.nextNodeKey
              ? byKey.has(option.nextNodeKey)
                ? walkChain(option.nextNodeKey, visited)
                : [{ node: fromOption(option) }]
              : [],
          })),
        });
        break;
      }

      items.push({ node });

      if (node.type === ScenarioNodeType.End) {
        break;
      }

      key = node.nextNodeKey || "";
    }

    return items;
  };

  const items = startNodeKey ? walkChain(startNodeKey, new Set()) : [];
  const orphans = list.filter((node) => !reached.has(node.key));

  return { items, orphans };
};

export const initNode = (
  nodes: ScenarioNode[],
  scenario: Scenario,
  type: ScenarioNodeType = ScenarioNodeType.Message,
  prevNodeKey = "",
  nextNodeKey = "",
  key?: string,
): ScenarioNode => {
  const nextId = Math.min(0, ...nodes.map((node) => node.id), 0) - 1;

  return {
    id: nextId,
    createdAt: 0,
    updatedAt: 0,
    isDeleted: false,
    deletedAt: 0,
    deletedBy: "",
    label: "",
    key: key || getNextNodeKey(nodes),
    type,
    content: "",
    variableKey: "",
    prevNodeKey,
    nextNodeKey,
    position: getNextNodePosition(nodes),
    options: [],
    scenario,
  };
};

export type AddTarget =
  | { kind: "branch"; choiceKey: string }
  | { kind: "fill"; choiceKey: string; optionIndex: number }
  | { kind: "insert"; anchorKey: string; beforeKey: string }
  | { kind: "append"; anchorKey: string };

export const getChainTail = (nodes: ScenarioNode[] | undefined, fromKey: string): ScenarioNode | null => {
  const byKey = new Map((nodes ?? []).map((node) => [node.key, node]));
  const visited = new Set<string>();
  let current = byKey.get(fromKey) ?? null;
  let last = current;

  while (current && !visited.has(current.key)) {
    visited.add(current.key);
    last = current;

    if (
      current.type === ScenarioNodeType.End ||
      current.type === ScenarioNodeType.Choice ||
      !current.nextNodeKey
    ) {
      break;
    }

    current = byKey.get(current.nextNodeKey) ?? null;
  }

  return last;
};

export const resolveAddTarget = (nodes: ScenarioNode[] | undefined, fromKey: string): AddTarget => {
  const list = nodes ?? [];

  if (!fromKey) {
    return { kind: "append", anchorKey: "" };
  }

  const tail = getChainTail(list, fromKey);

  if (!tail) {
    return { kind: "append", anchorKey: "" };
  }

  if (tail.type === ScenarioNodeType.Choice) {
    return { kind: "branch", choiceKey: tail.key };
  }

  if (tail.type === ScenarioNodeType.End) {
    const parent = findParent(list, tail.key);

    return {
      kind: "insert",
      anchorKey: parent?.node.key ?? tail.prevNodeKey,
      beforeKey: tail.key,
    };
  }

  return { kind: "append", anchorKey: tail.key };
};

export const resolveBranchTarget = (
  nodes: ScenarioNode[] | undefined,
  choiceKey: string,
  optionIndex: number,
): AddTarget => {
  const list = nodes ?? [];
  const choice = list.find((node) => node.key === choiceKey);
  const option = choice?.options?.[optionIndex];
  const targetKey = option?.nextNodeKey ?? "";
  const targetNode = targetKey ? list.find((node) => node.key === targetKey) : undefined;

  if (!targetKey || !targetNode) {
    return { kind: "fill", choiceKey, optionIndex };
  }

  const tail = getChainTail(list, targetKey);

  if (!tail) {
    return { kind: "fill", choiceKey, optionIndex };
  }

  if (tail.type === ScenarioNodeType.Choice) {
    return { kind: "branch", choiceKey: tail.key };
  }

  if (tail.type === ScenarioNodeType.End) {
    const parent = findParent(list, tail.key);

    return {
      kind: "insert",
      anchorKey: parent?.node.key ?? choiceKey,
      beforeKey: tail.key,
    };
  }

  return { kind: "append", anchorKey: tail.key };
};

export const buildNewNodes = (
  nodes: ScenarioNode[],
  scenario: Scenario,
  type: ScenarioNodeType,
  target: AddTarget,
): ScenarioNode[] => {
  const prevNodeKey =
    target.kind === "branch" || target.kind === "fill" ? target.choiceKey : target.anchorKey;
  const nextOnInsert = target.kind === "insert" ? target.beforeKey : "";
  const withPair = (target.kind === "branch" || target.kind === "fill") &&
    type !== ScenarioNodeType.Choice &&
    type !== ScenarioNodeType.End;

  if (type === ScenarioNodeType.Choice && target.kind === "insert" && nextOnInsert) {
    const created = initNode(nodes, scenario, type, prevNodeKey, "");

    return [
      {
        ...created,
        options: [{ label: "Lựa chọn mới", nextNodeKey: nextOnInsert, position: 0 }],
      },
    ];
  }

  const head = initNode(nodes, scenario, type, prevNodeKey, withPair ? "" : nextOnInsert);

  if (!withPair) {
    return [head];
  }

  const endNode = initNode([...nodes, head], scenario, ScenarioNodeType.End, head.key, "");

  return [{ ...head, nextNodeKey: endNode.key }, endNode];
};

export const insertNodes = (nodes: ScenarioNode[], created: ScenarioNode[], target: AddTarget): ScenarioNode[] => {
  const head = created[0];
  const last = created[created.length - 1];
  const nextNodes = [...nodes, ...created];

  if (!head) {
    return nodes;
  }

  switch (target.kind) {
    case "branch":
      return nextNodes.map((item) => {
        if (item.key !== target.choiceKey) {
          return item;
        }

        return {
          ...item,
          options: [
            ...(item.options ?? []),
            {
              label: "Lựa chọn mới",
              nextNodeKey: head.key,
              position: item.options?.length ?? 0,
            },
          ],
        };
      });
    case "fill":
      return nextNodes.map((item) => {
        if (item.key !== target.choiceKey) {
          return item;
        }

        return {
          ...item,
          options: (item.options ?? []).map((option, index) =>
            index === target.optionIndex ? { ...option, nextNodeKey: head.key } : option,
          ),
        };
      });
    case "append":
      if (!target.anchorKey) {
        return nextNodes;
      }

      return nextNodes.map((item) => {
        if (item.key !== target.anchorKey || item.type === ScenarioNodeType.Choice) {
          return item;
        }

        return { ...item, nextNodeKey: head.key };
      });
    case "insert":
      return nextNodes.map((item) => {
        if (item.key === target.anchorKey) {
          if (item.type === ScenarioNodeType.Choice) {
            return {
              ...item,
              options: (item.options ?? []).map((option) =>
                option.nextNodeKey === target.beforeKey ? { ...option, nextNodeKey: head.key } : option,
              ),
            };
          }

          return { ...item, nextNodeKey: head.key };
        }

        if (item.key === target.beforeKey) {
          return { ...item, prevNodeKey: last.key };
        }

        return item;
      });
    default:
      return nextNodes;
  }
};

export const removeLocalNode = (
  nodes: ScenarioNode[],
  serverNodes: ScenarioNode[] | undefined,
  node: ScenarioNode,
) => {
  const parent = findParent(nodes, node.key);

  return nodes
    .filter((item) => item.id !== node.id)
    .map((item) => {
      if (parent && item.id === parent.node.id && parent.optionIndex >= 0) {
        const serverChoice = serverNodes?.find((origin) => origin.id === item.id);
        const serverOption = serverChoice?.options?.[parent.optionIndex];

        if (!serverOption) {
          return {
            ...item,
            options: (item.options ?? []).filter((_, index) => index !== parent.optionIndex),
          };
        }

        return {
          ...item,
          options: (item.options ?? []).map((option, index) => {
            if (index !== parent.optionIndex || option.nextNodeKey !== node.key) {
              return option;
            }

            return { ...option, nextNodeKey: node.nextNodeKey || "" };
          }),
        };
      }

      if (item.nextNodeKey === node.key) {
        return { ...item, nextNodeKey: node.nextNodeKey };
      }

      if (node.nextNodeKey && item.key === node.nextNodeKey) {
        return { ...item, prevNodeKey: parent?.node.key ?? node.prevNodeKey };
      }

      return item;
    });
};

export const getDraftsInOrder = (nodes: ScenarioNode[], startNodeKey: string) => {
  const byKey = new Map(nodes.map((node) => [node.key, node]));
  const visited = new Set<string>();
  const drafts: ScenarioNode[] = [];

  const walk = (key: string) => {
    if (!key || visited.has(key) || !byKey.has(key)) {
      return;
    }

    visited.add(key);
    const node = byKey.get(key) as ScenarioNode;

    if (isNewNode(node)) {
      drafts.push(node);
    }

    if (node.type === ScenarioNodeType.Choice) {
      (node.options ?? []).forEach((option) => walk(option.nextNodeKey));
      return;
    }

    walk(node.nextNodeKey);
  };

  walk(startNodeKey);

  nodes.forEach((node) => {
    if (!visited.has(node.key) && isNewNode(node)) {
      drafts.push(node);
    }
  });

  return drafts;
};

export const resolveScenarioId = (prev: number | null, scenarios: Scenario[]) => {
  if (!scenarios.length) {
    return null;
  }

  return scenarios.some((scenario) => scenario.id === prev) ? prev : scenarios[0].id;
};
